import React, { useState, useMemo } from 'react';
import { Card, Table, Button } from 'react-bootstrap';
import { FaEye, FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import { COLUNAS_ATIVOS, PAGE_SIZE } from './constants';

const paginationStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  button: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    backgroundColor: '#e9ecef',
    color: '#495057',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    userSelect: 'none',
    padding: 0,
    lineHeight: 1,
  },
  buttonActive: {
    backgroundColor: '#419AEF',
    color: '#E5E6E7',
    border: '1px solid #419AEF',
    fontWeight: 600,
  },
  buttonDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
};

function PaginacaoCustom({ pagina, totalPaginas, onChange }) {
  if (totalPaginas <= 1) return null;

  let inicio = Math.max(1, pagina - 2);
  let fim = Math.min(totalPaginas, inicio + 4);
  if (fim - inicio < 4) {
    inicio = Math.max(1, fim - 4);
  }

  const paginas = [];
  for (let i = inicio; i <= fim; i++) {
    paginas.push(i);
  }

  const btnStyle = (active, disabled) => ({
    ...paginationStyles.button,
    ...(active ? paginationStyles.buttonActive : {}),
    ...(disabled ? paginationStyles.buttonDisabled : {}),
  });

  return (
    <div style={paginationStyles.container}>
      <button
        style={btnStyle(false, pagina === 1)}
        onClick={() => pagina > 1 && onChange(1)}
        disabled={pagina === 1}
        title="Primeira"
      >
        «
      </button>
      <button
        style={btnStyle(false, pagina === 1)}
        onClick={() => pagina > 1 && onChange(pagina - 1)}
        disabled={pagina === 1}
        title="Anterior"
      >
        ‹
      </button>

      {paginas.map(pg => (
        <button
          key={pg}
          style={btnStyle(pg === pagina, false)}
          onClick={() => onChange(pg)}
        >
          {pg}
        </button>
      ))}

      <button
        style={btnStyle(false, pagina === totalPaginas)}
        onClick={() => pagina < totalPaginas && onChange(pagina + 1)}
        disabled={pagina === totalPaginas}
        title="Proxima"
      >
        ›
      </button>
      <button
        style={btnStyle(false, pagina === totalPaginas)}
        onClick={() => pagina < totalPaginas && onChange(totalPaginas)}
        disabled={pagina === totalPaginas}
        title="Ultima"
      >
        »
      </button>
    </div>
  );
}

export default function TabelaAtivos({ dados, nomeEmpreendimento, onDetalhar }) {
  const [pagina, setPagina] = useState(1);
  const [sortKey, setSortKey] = useState('validade');
  const [sortDir, setSortDir] = useState('desc');

  const dadosOrdenados = useMemo(() => {
    if (!dados) return [];
    const sorted = [...dados].sort((a, b) => {
      const valA = a[sortKey] != null ? a[sortKey] : '';
      const valB = b[sortKey] != null ? b[sortKey] : '';
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [dados, sortKey, sortDir]);

  const totalPaginas = Math.ceil(dadosOrdenados.length / PAGE_SIZE);
  const dadosPaginados = dadosOrdenados.slice(
    (pagina - 1) * PAGE_SIZE,
    pagina * PAGE_SIZE
  );

  const handleSort = key => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPagina(1);
  };

  const renderSortIcon = key => {
    if (sortKey !== key) return <FaSort className="ml-1 text-muted" />;
    return sortDir === 'asc' ? (
      <FaSortUp className="ml-1" />
    ) : (
      <FaSortDown className="ml-1" />
    );
  };

  const formatDate = dateStr => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <Card className="mb-2 shadow-sm" style={{ borderTop: '3px solid #0747a6' }}>
      <Card.Body className="py-2 px-3">
        <h6 className="mb-2" style={{ color: '#0747a6', fontSize: '0.9rem' }}>
          {nomeEmpreendimento || 'Ativos encontrados'}
        </h6>

        <div className="table-responsive" style={{ borderRadius: '8px', overflow: 'hidden' }}>
          <Table hover size="sm" style={{ marginBottom: 0 }}>
            <thead style={{ backgroundColor: '#0747a6', color: '#fff' }}>
              <tr>
                {COLUNAS_ATIVOS.map(col => (
                  <th
                    key={col.key}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'center' }}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label} {renderSortIcon(col.key)}
                  </th>
                ))}
                <th style={{ textAlign: 'center' }}>Detalhar</th>
              </tr>
            </thead>
            <tbody>
              {dadosPaginados.map((item, idx) => (
                <tr key={idx}>
                  <td className="text-center">{item.empreendimento}</td>
                  <td className="text-center">{item.quadra}</td>
                  <td className="text-center">{item.lote}</td>
                  <td>{item.corretor}</td>
                  <td className="text-center">{item.imobiliaria}</td>
                  <td className="text-center">{formatDate(item.emissao)}</td>
                  <td className="text-center">{formatDate(item.validade)}</td>
                  <td className="text-center">{item.fila}</td>
                  <td className="text-center">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => onDetalhar(item)}
                    >
                      <FaEye />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {dadosPaginados.length === 0 && (
          <div className="text-center py-3">
            <span className="badge badge-info">
              Nao ha resultados para o(s) filtro(s) informado(s).
            </span>
          </div>
        )}

        {totalPaginas > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">
              Mostrando {(pagina - 1) * PAGE_SIZE + 1} -{' '}
              {Math.min(pagina * PAGE_SIZE, dadosOrdenados.length)} de{' '}
              {dadosOrdenados.length} registros
            </small>
            <PaginacaoCustom
              pagina={pagina}
              totalPaginas={totalPaginas}
              onChange={setPagina}
            />
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
